import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, X, Search, Users, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export interface Collaborator {
  id?: string;
  user_id: string;
  percentage: number;
  profile?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  };
}

interface CollaboratorPickerProps {
  collaborators: Collaborator[];
  onCollaboratorsChange: (collaborators: Collaborator[]) => void;
  maxTotalPercentage?: number; // Default 99 to ensure owner gets at least 1%
}

interface UserSearchResult {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  school: string | null;
}

export function CollaboratorPicker({ 
  collaborators, 
  onCollaboratorsChange,
  maxTotalPercentage = 99 
}: CollaboratorPickerProps) {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const totalPercentage = collaborators.reduce((sum, c) => sum + c.percentage, 0);
  const ownerPercentage = 100 - totalPercentage;

  useEffect(() => {
    const searchUsers = async () => {
      if (!searchQuery.trim() || searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, avatar_url, school')
          .or(`first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%`)
          .neq('id', user?.id || '')
          .limit(10);

        if (error) throw error;

        // Filter out already added collaborators
        const existingIds = new Set(collaborators.map(c => c.user_id));
        const filtered = (data || []).filter(u => !existingIds.has(u.id));
        setSearchResults(filtered);
      } catch (error) {
        console.error("Error searching users:", error);
      } finally {
        setIsSearching(false);
      }
    };

    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, user?.id, collaborators]);

  const addCollaborator = (userResult: UserSearchResult) => {
    const remainingPercentage = maxTotalPercentage - totalPercentage;
    if (remainingPercentage <= 0) {
      toast.error("Cannot add more collaborators - percentage limit reached");
      return;
    }

    const newCollaborator: Collaborator = {
      user_id: userResult.id,
      percentage: Math.min(10, remainingPercentage), // Default 10% or what's available
      profile: {
        id: userResult.id,
        first_name: userResult.first_name,
        last_name: userResult.last_name,
        avatar_url: userResult.avatar_url,
      },
    };

    onCollaboratorsChange([...collaborators, newCollaborator]);
    setSearchQuery("");
    setSearchResults([]);
    setIsDialogOpen(false);
  };

  const removeCollaborator = (userId: string) => {
    onCollaboratorsChange(collaborators.filter(c => c.user_id !== userId));
  };

  const updatePercentage = (userId: string, percentage: number) => {
    const otherTotal = collaborators
      .filter(c => c.user_id !== userId)
      .reduce((sum, c) => sum + c.percentage, 0);
    
    const maxAllowed = maxTotalPercentage - otherTotal;
    const clampedPercentage = Math.min(Math.max(1, percentage), maxAllowed);

    onCollaboratorsChange(
      collaborators.map(c => 
        c.user_id === userId 
          ? { ...c, percentage: clampedPercentage }
          : c
      )
    );
  };

  const getDisplayName = (profile?: Collaborator['profile']) => {
    if (!profile) return "Unknown User";
    const firstName = profile.first_name || "";
    const lastName = profile.last_name || "";
    return `${firstName} ${lastName}`.trim() || "Unknown User";
  };

  const getInitials = (profile?: Collaborator['profile']) => {
    if (!profile) return "?";
    const first = profile.first_name?.[0] || "";
    const last = profile.last_name?.[0] || "";
    return `${first}${last}`.toUpperCase() || "?";
  };

  return (
    <Card className="border border-border bg-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="h-5 w-5 text-anyskill-blue" />
          Collaborators
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Add collaborators who will receive a percentage of each payment for this service.
        </p>

        {/* Owner share display */}
        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Your share (owner)</span>
          </div>
          <span className={`text-sm font-bold ${ownerPercentage < 1 ? 'text-destructive' : 'text-foreground'}`}>
            {ownerPercentage.toFixed(0)}%
          </span>
        </div>

        {ownerPercentage < 1 && (
          <div className="flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>Owner must receive at least 1%</span>
          </div>
        )}

        {/* Current collaborators */}
        {collaborators.length > 0 && (
          <div className="space-y-2">
            {collaborators.map((collaborator) => (
              <div 
                key={collaborator.user_id}
                className="flex items-center gap-3 p-3 bg-background border border-border rounded-lg"
              >
                <Avatar className="h-10 w-10">
                  <AvatarImage src={collaborator.profile?.avatar_url || undefined} />
                  <AvatarFallback className="bg-anyskill-purple/10 text-anyskill-purple">
                    {getInitials(collaborator.profile)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {getDisplayName(collaborator.profile)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={maxTotalPercentage}
                    value={collaborator.percentage}
                    onChange={(e) => updatePercentage(collaborator.user_id, Number(e.target.value))}
                    className="w-20 text-center"
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeCollaborator(collaborator.user_id)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add collaborator button */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              variant="outline" 
              className="w-full"
              disabled={totalPercentage >= maxTotalPercentage}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Collaborator
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Collaborator</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <ScrollArea className="h-64">
                {isSearching ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
                  </div>
                ) : searchResults.length > 0 ? (
                  <div className="space-y-2">
                    {searchResults.map((result) => (
                      <button
                        key={result.id}
                        onClick={() => addCollaborator(result)}
                        className="w-full flex items-center gap-3 p-3 hover:bg-muted rounded-lg transition-colors text-left"
                      >
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={result.avatar_url || undefined} />
                          <AvatarFallback className="bg-anyskill-purple/10 text-anyskill-purple">
                            {(result.first_name?.[0] || "") + (result.last_name?.[0] || "")}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">
                            {`${result.first_name || ""} ${result.last_name || ""}`.trim() || "Unknown User"}
                          </p>
                          {result.school && (
                            <p className="text-sm text-muted-foreground truncate">
                              {result.school}
                            </p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : searchQuery.length >= 2 ? (
                  <p className="text-center py-8 text-muted-foreground">
                    No users found
                  </p>
                ) : (
                  <p className="text-center py-8 text-muted-foreground">
                    Type at least 2 characters to search
                  </p>
                )}
              </ScrollArea>
            </div>
          </DialogContent>
        </Dialog>

        {collaborators.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Total distributed: {totalPercentage}% • Remaining for you: {ownerPercentage}%
          </p>
        )}
      </CardContent>
    </Card>
  );
}
